import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
import ChartHelpButton from '../ChartHelpButton';
import { getCleanOptions, pickBestFobCombination } from '../../utils/analyticsHelpers';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { usePersistWidgetSnapshot } from '../../hooks/usePersistWidgetSnapshot';
import { useWidgetOptionsPersistence } from '../../hooks/useWidgetOptionsPersistence';
import { singleSelectionLabel } from '../../../utils/filterLabels';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * FOB Price by Origin Heatmap
 * Grid showing price by origin/month with color-coded changes vs previous year
 *
 * Uses cascading filters - each filter narrows down available options for subsequent filters
 */
const FOBPriceHeatmap = ({ data = [], origins = [], years = [], commodity = '' }) => {
  const { isReady: analyticsPrefsReady } = useWidgetOptionsPersistence('fob-price-heatmap');
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [compareYear, setCompareYear] = useState(null);
  const [granularity, setGranularity] = useState('monthly');
  const [selectedOrigins, setSelectedOrigins] = useState([]);

  // Modal states
  const [yearModalOpen, setYearModalOpen] = useState(false);
  const [compareYearModalOpen, setCompareYearModalOpen] = useState(false);
  const [originModalOpen, setOriginModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [varietyModalOpen, setVarietyModalOpen] = useState(false);
  const [sizeModalOpen, setSizeModalOpen] = useState(false);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [storageModalOpen, setStorageModalOpen] = useState(false);
  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  const [environmentModalOpen, setEnvironmentModalOpen] = useState(false);
  const [qualityModalOpen, setQualityModalOpen] = useState(false);
  const [appearanceModalOpen, setAppearanceModalOpen] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);

  // Product characteristic filters (single-select)
  const [selectedType, setSelectedType] = useState('Conventional');
  const [selectedVariety, setSelectedVariety] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [selectedStorage, setSelectedStorage] = useState('');
  const [selectedCondition, setSelectedCondition] = useState('');
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [selectedQuality, setSelectedQuality] = useState('');
  const [selectedAppearance, setSelectedAppearance] = useState('');
  const [selectedProperties, setSelectedProperties] = useState('');

  // Track if initial cascade has run
  const initializedRef = useRef(false);

  // Sort years descending (most recent first)
  const sortedYears = useMemo(() => [...years].sort((a, b) => b - a), [years]);

  // Calculate all filter options based on current selections (for display in modals)
  // Cascade order: Year → Origins → Type → Variety → Size → Grade → Package → Storage → Condition → Environment → Quality → Appearance → Properties
  const availableOptions = useMemo(() => {
    if (!data || data.length === 0) {
      return { origins: [], types: [], varieties: [], sizes: [], grades: [], packages: [],
               storages: [], conditions: [], environments: [], qualities: [],
               appearances: [], properties: [] };
    }

    let filtered = data;

    // Filter by year first
    if (selectedYear) {
      filtered = filtered.filter(item => item.year === selectedYear);
    }
    const origins = getCleanOptions(filtered, 'origin', commodity);

    // Filter by origins
    if (selectedOrigins.length > 0) {
      filtered = filtered.filter(item => selectedOrigins.includes(item.origin));
    }
    const types = getCleanOptions(filtered, 'type', commodity);

    // Filter by type
    if (selectedType) {
      filtered = filtered.filter(item => item.type === selectedType);
    }
    const varieties = getCleanOptions(filtered, 'variety', commodity);

    // Filter by variety
    if (selectedVariety) {
      filtered = filtered.filter(item => item.variety === selectedVariety);
    }
    const sizes = getCleanOptions(filtered, 'item_size', commodity);

    // Filter by size
    if (selectedSize) {
      filtered = filtered.filter(item => item.item_size === selectedSize);
    }
    const grades = getCleanOptions(filtered, 'grade', commodity);

    // Filter by grade
    if (selectedGrade) {
      filtered = filtered.filter(item => item.grade === selectedGrade);
    }
    const packages = getCleanOptions(filtered, 'package', commodity);

    // Filter by package
    if (selectedPackage) {
      filtered = filtered.filter(item => item.package === selectedPackage);
    }
    const storages = getCleanOptions(filtered, 'storage', commodity);

    // Filter by storage
    if (selectedStorage) {
      filtered = filtered.filter(item => item.storage === selectedStorage);
    }
    const conditions = getCleanOptions(filtered, 'condition', commodity);

    // Filter by condition
    if (selectedCondition) {
      filtered = filtered.filter(item => item.condition === selectedCondition);
    }
    const environments = getCleanOptions(filtered, 'environment', commodity);

    // Filter by environment
    if (selectedEnvironment) {
      filtered = filtered.filter(item => item.environment === selectedEnvironment);
    }
    const qualities = getCleanOptions(filtered, 'quality', commodity);

    // Filter by quality
    if (selectedQuality) {
      filtered = filtered.filter(item => item.quality === selectedQuality);
    }
    const appearances = getCleanOptions(filtered, 'appearance', commodity);

    // Filter by appearance
    if (selectedAppearance) {
      filtered = filtered.filter(item => item.appearance === selectedAppearance);
    }
    const properties = getCleanOptions(filtered, 'properties', commodity);

    return {
      origins, types, varieties, sizes, grades, packages,
      storages, conditions, environments, qualities,
      appearances, properties
    };
  }, [data, selectedYear, selectedOrigins, selectedType, selectedVariety, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality, selectedAppearance, commodity]);

  /**
   * Default filter combination for a year/origins scope.
   * Order: Year → Origins → Type → Variety → Size → Grade → Package → Storage → Condition → Environment → Quality → Appearance → Properties
   *
   * The chain still describes how the filter UI cascades, but the values come
   * from scoring whole combinations by how many cells they actually fill (see
   * `pickBestFobCombination`). This previously took the first valid option at
   * every level — alphabetical order, unrelated to where the data is — which
   * routinely opened the heatmap on a near-empty grid.
   */
  const calculateCascade = useCallback((startingData, options = {}) => {
    const { year = null, origins = null, type = null, granularity: scoreGranularity = 'monthly' } = options;
    let filtered = startingData;

    // Apply year filter if provided
    if (year) {
      filtered = filtered.filter(item => item.year === year);
    }

    // Get available origins for this year
    const availableOrigins = getCleanOptions(filtered, 'origin', commodity);
    // If origins provided, use them; otherwise select all
    const selectedOrigs = origins || availableOrigins;
    if (selectedOrigs && selectedOrigs.length > 0) {
      filtered = filtered.filter(item => selectedOrigs.includes(item.origin));
    }

    // A caller-pinned type is honoured, but only where it has rows — pinning a
    // type the commodity does not carry empties the slice and blanks every
    // downstream default.
    if (type) {
      const withType = filtered.filter(item => item.type === type);
      if (withType.length > 0) filtered = withType;
    }

    return {
      origins: availableOrigins,
      ...pickBestFobCombination(filtered, { granularity: scoreGranularity, commodity }),
    };
  }, []);

  // Do NOT clear initializedRef in a useEffect([commodity]): effects run after useLayoutEffect, so
  // this would run after Firestore hydrate (usePersistWidgetSnapshot) and wipe initializedRef,
  // letting the init cascade below apply default years and overwrite saved state + Firebase.

  const hydrateFOBHeatmap = useCallback(
    (s) => {
      initializedRef.current = true;
      if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
      if (s.granularity === 'monthly' || s.granularity === 'weekly') setGranularity(s.granularity);

      const yRaw = s.selectedYear;
      const yNum = typeof yRaw === 'number' ? yRaw : Number(yRaw);
      const y =
        Number.isFinite(yNum) && sortedYears.includes(yNum) ? yNum : sortedYears[0];
      const cyRaw = s.compareYear;
      const cyNum = typeof cyRaw === 'number' ? cyRaw : Number(cyRaw);
      const cy =
        Number.isFinite(cyNum) && sortedYears.includes(cyNum)
          ? cyNum
          : sortedYears[1] || sortedYears[0];
      setSelectedYear(y);
      setCompareYear(cy);

      let filtered = data.filter((item) => item.year === y);
      const originOpts = getCleanOptions(filtered, 'origin', commodity);
      let nextOrigins = Array.isArray(s.selectedOrigins)
        ? s.selectedOrigins.filter((o) => originOpts.includes(o))
        : [];
      if (nextOrigins.length === 0) nextOrigins = originOpts;
      setSelectedOrigins(nextOrigins);
      if (nextOrigins.length > 0) {
        filtered = filtered.filter((item) => nextOrigins.includes(item.origin));
      }

            // Every load opens on the combination with the most data, ignoring the
      // characteristics saved from the last visit — restoring them brought a
      // user back to whatever thin slice they last drilled into, which reads
      // as a broken chart. Filtering is a within-session action.
      const restored = pickBestFobCombination(filtered, { granularity: s.granularity === 'weekly' ? 'weekly' : 'monthly', commodity });

      setSelectedType(restored.type);
      setSelectedVariety(restored.variety);
      setSelectedSize(restored.size);
      setSelectedGrade(restored.grade);
      setSelectedPackage(restored.package);
      setSelectedStorage(restored.storage);
      setSelectedCondition(restored.condition);
      setSelectedEnvironment(restored.environment);
      setSelectedQuality(restored.quality);
      setSelectedAppearance(restored.appearance);
      setSelectedProperties(restored.properties);
    },
    [data, sortedYears, commodity]
  );

  const snapshotFOBHeatmap = useCallback(
    () => ({
      filtersExpanded,
      granularity,
      selectedYear,
      compareYear,
      selectedOrigins,
      selectedType,
      selectedVariety,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    }),
    [
      filtersExpanded,
      granularity,
      selectedYear,
      compareYear,
      selectedOrigins,
      selectedType,
      selectedVariety,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    ]
  );

  usePersistWidgetSnapshot(
    'fob-price-heatmap',
    commodity,
    !!(data && data.length > 0 && sortedYears.length > 0),
    hydrateFOBHeatmap,
    snapshotFOBHeatmap,
    [
      filtersExpanded,
      granularity,
      selectedYear,
      compareYear,
      selectedOrigins,
      selectedType,
      selectedVariety,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    ]
  );

  // Default cascade only when there is no Firestore snapshot to hydrate (see usePersistWidgetSnapshot).
  // Must run only after analytics state has been fetched once; otherwise this runs before hydrate,
  // overwrites year/compareYear and all filters, then persist saves that to Firebase.
  useEffect(() => {
    if (!analyticsPrefsReady || !data || data.length === 0 || initializedRef.current) return;

    initializedRef.current = true;

    const defaultYear = sortedYears[0];
    const defaultCompareYear = sortedYears[1] || sortedYears[0];
    setSelectedYear(defaultYear);
    setCompareYear(defaultCompareYear);

    const cascade = calculateCascade(data, { year: defaultYear });
    setSelectedOrigins(cascade.origins);
    setSelectedType(cascade.type);
    setSelectedVariety(cascade.variety);
    setSelectedSize(cascade.size);
    setSelectedGrade(cascade.grade);
    setSelectedPackage(cascade.package);
    setSelectedStorage(cascade.storage);
    setSelectedCondition(cascade.condition);
    setSelectedEnvironment(cascade.environment);
    setSelectedQuality(cascade.quality);
    setSelectedAppearance(cascade.appearance);
    setSelectedProperties(cascade.properties);
  }, [analyticsPrefsReady, data, sortedYears, calculateCascade]);

  // Handler for YEAR change - cascades origins, type, and all downstream (keep-if-valid)
  // Handler for YEAR change - re-derives origins, type and all downstream for coverage
  const handleYearChange = useCallback((newYear) => {
    const yearNum = Number(newYear);
    setSelectedYear(yearNum);

    const cascade = calculateCascade(data, { year: yearNum, granularity });
    setSelectedOrigins(cascade.origins);
    setSelectedType(cascade.type);
    setSelectedVariety(cascade.variety);
    setSelectedSize(cascade.size);
    setSelectedGrade(cascade.grade);
    setSelectedPackage(cascade.package);
    setSelectedStorage(cascade.storage);
    setSelectedCondition(cascade.condition);
    setSelectedEnvironment(cascade.environment);
    setSelectedQuality(cascade.quality);
    setSelectedAppearance(cascade.appearance);
    setSelectedProperties(cascade.properties);
  }, [data, granularity, calculateCascade]);

  // Handler for ORIGINS change - re-derives type and all downstream for coverage
  const handleOriginsChange = useCallback((newOrigins) => {
    setSelectedOrigins(newOrigins);

    const cascade = calculateCascade(data, { year: selectedYear, origins: newOrigins, granularity });
    setSelectedType(cascade.type);
    setSelectedVariety(cascade.variety);
    setSelectedSize(cascade.size);
    setSelectedGrade(cascade.grade);
    setSelectedPackage(cascade.package);
    setSelectedStorage(cascade.storage);
    setSelectedCondition(cascade.condition);
    setSelectedEnvironment(cascade.environment);
    setSelectedQuality(cascade.quality);
    setSelectedAppearance(cascade.appearance);
    setSelectedProperties(cascade.properties);
  }, [data, selectedYear, granularity, calculateCascade]);

  // Handler for TYPE change - re-derives variety and all downstream for coverage
  const handleTypeChange = useCallback((newType) => {
    setSelectedType(newType);

    const cascade = calculateCascade(data, {
      year: selectedYear, origins: selectedOrigins, type: newType, granularity,
    });
    setSelectedVariety(cascade.variety);
    setSelectedSize(cascade.size);
    setSelectedGrade(cascade.grade);
    setSelectedPackage(cascade.package);
    setSelectedStorage(cascade.storage);
    setSelectedCondition(cascade.condition);
    setSelectedEnvironment(cascade.environment);
    setSelectedQuality(cascade.quality);
    setSelectedAppearance(cascade.appearance);
    setSelectedProperties(cascade.properties);
  }, [data, selectedYear, selectedOrigins, granularity, calculateCascade]);

  // Generic cascade handler for any filter level (variety and below)
  const handleFilterChange = useCallback((level, newValue) => {
    // Build filtered data starting from year → origins → type
    let filtered = data;
    if (selectedYear) filtered = filtered.filter(item => item.year === selectedYear);
    if (selectedOrigins.length > 0) filtered = filtered.filter(item => selectedOrigins.includes(item.origin));
    if (selectedType) filtered = filtered.filter(item => item.type === selectedType);

    const filterOrder = ['variety', 'size', 'grade', 'package', 'storage', 'condition', 'environment', 'quality', 'appearance', 'properties'];
    const filterFields = {
      variety: 'variety',
      size: 'item_size',
      grade: 'grade',
      package: 'package',
      storage: 'storage',
      condition: 'condition',
      environment: 'environment',
      quality: 'quality',
      appearance: 'appearance',
      properties: 'properties'
    };
    const currentValues = {
      variety: selectedVariety,
      size: selectedSize,
      grade: selectedGrade,
      package: selectedPackage,
      storage: selectedStorage,
      condition: selectedCondition,
      environment: selectedEnvironment,
      quality: selectedQuality,
      appearance: selectedAppearance,
      properties: selectedProperties
    };
    const setters = {
      variety: setSelectedVariety,
      size: setSelectedSize,
      grade: setSelectedGrade,
      package: setSelectedPackage,
      storage: setSelectedStorage,
      condition: setSelectedCondition,
      environment: setSelectedEnvironment,
      quality: setSelectedQuality,
      appearance: setSelectedAppearance,
      properties: setSelectedProperties
    };

    const levelIdx = filterOrder.indexOf(level);

    // Apply all filters up to (but not including) this level
    for (let i = 0; i < levelIdx; i++) {
      const l = filterOrder[i];
      const field = filterFields[l];
      const value = currentValues[l];
      if (value) {
        filtered = filtered.filter(item => item[field] === value);
      }
    }

    // Apply the new value for this level
    setters[level](newValue);

    if (newValue) {
      filtered = filtered.filter(item => item[filterFields[level]] === newValue);
    }

    // Cascade downstream: score the remaining fields as a whole combination.
    // Keeping a value because it still appears in the new dropdown is not the
    // same as it still having data — that is how the grid ended up nearly
    // empty while every filter still looked reasonable.
    const best = pickBestFobCombination(filtered, { granularity, commodity });
    for (let i = levelIdx + 1; i < filterOrder.length; i++) {
      const l = filterOrder[i];
      setters[l](best[l] || '');
    }
  }, [data, selectedYear, selectedOrigins, selectedType, selectedVariety, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality,
      selectedAppearance, selectedProperties, granularity, commodity]);

  // Filter data by all selected characteristics
  const filteredData = useMemo(() => {
    return data.filter(item => {
      if (selectedType && item.type !== selectedType) return false;
      if (selectedVariety && item.variety !== selectedVariety) return false;
      if (selectedSize && item.item_size !== selectedSize) return false;
      if (selectedGrade && item.grade !== selectedGrade) return false;
      if (selectedPackage && item.package !== selectedPackage) return false;
      if (selectedStorage && item.storage !== selectedStorage) return false;
      if (selectedCondition && item.condition !== selectedCondition) return false;
      if (selectedEnvironment && item.environment !== selectedEnvironment) return false;
      if (selectedQuality && item.quality !== selectedQuality) return false;
      if (selectedAppearance && item.appearance !== selectedAppearance) return false;
      if (selectedProperties && item.properties !== selectedProperties) return false;
      return true;
    });
  }, [data, selectedType, selectedVariety, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality,
      selectedAppearance, selectedProperties]);

  // Aggregate raw data by origin and period (month or week)
  const aggregatedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return {};

    const byOrigin = {};
    filteredData.forEach(item => {
      const origin = item.origin;
      const year = item.year;
      const period = granularity === 'weekly' ? item.iso_week : item.month;
      const price = item.avg_price;

      if (!price || !origin || !period) return;

      if (!byOrigin[origin]) byOrigin[origin] = {};
      if (!byOrigin[origin][year]) byOrigin[origin][year] = {};
      if (!byOrigin[origin][year][period]) byOrigin[origin][year][period] = { prices: [] };

      byOrigin[origin][year][period].prices.push(price);
    });

    // Calculate averages
    Object.values(byOrigin).forEach(yearData => {
      Object.values(yearData).forEach(periodData => {
        Object.values(periodData).forEach(data => {
          if (data.prices.length > 0) {
            data.avg = data.prices.reduce((a, b) => a + b, 0) / data.prices.length;
          }
        });
      });
    });

    return byOrigin;
  }, [filteredData, granularity]);

  // Get period labels and count based on granularity
  const { periodLabels, periodCount } = useMemo(() => {
    if (granularity === 'weekly') {
      const labels = [];
      for (let i = 1; i <= 52; i++) {
        labels.push(`W${i}`);
      }
      return { periodLabels: labels, periodCount: 52 };
    }
    return { periodLabels: MONTH_LABELS, periodCount: 12 };
  }, [granularity]);

  // Process aggregated data into heatmap format
  const heatmapData = useMemo(() => {
    if (Object.keys(aggregatedData).length === 0 || selectedYear === null) return {};

    const result = {};

    selectedOrigins.forEach(origin => {
      if (!aggregatedData[origin]) return;
      result[origin] = {};

      for (let period = 1; period <= periodCount; period++) {
        const currentData = aggregatedData[origin][selectedYear]?.[period];
        const compareData = aggregatedData[origin][compareYear]?.[period];

        const currentPrice = currentData?.avg;
        const comparePrice = compareData?.avg;

        let delta = null;
        if (comparePrice && currentPrice) {
          delta = ((currentPrice - comparePrice) / comparePrice) * 100;
        }

        result[origin][period] = {
          price: currentPrice,
          delta,
          hasData: currentPrice !== null && currentPrice !== undefined
        };
      }
    });

    return result;
  }, [aggregatedData, selectedYear, compareYear, selectedOrigins, periodCount]);

  const getHeatmapColor = (delta) => {
    if (delta === null || delta === undefined) return isDark ? 'bg-[#2a2d2b]' : 'bg-gray-200';
    if (delta <= -10) return isDark ? 'bg-[#1a4d3a]' : 'bg-green-100';
    if (delta <= -2) return isDark ? 'bg-[#2a5c4a]' : 'bg-green-200';
    if (delta < 2) return isDark ? 'bg-[#3a3d3b]' : 'bg-gray-300';
    if (delta < 10) return isDark ? 'bg-[#5c3a3a]' : 'bg-red-200';
    return isDark ? 'bg-[#6b2d2d]' : 'bg-red-300';
  };

  const handleOriginToggle = (origin) => {
    if (selectedOrigins.includes(origin)) {
      setSelectedOrigins(selectedOrigins.filter(o => o !== origin));
    } else {
      setSelectedOrigins([...selectedOrigins, origin]);
    }
  };

  const helpDescription = "Heatmap comparing FOB prices by origin across months. Colors indicate price changes versus the comparison year - green means lower prices, red means higher prices.";

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex flex-col bg-transparent">
        <div className="border-y py-2 px-4 flex-shrink-0" style={{ borderColor: theme.borderColor }}>
          <div className="flex items-center gap-1">
            <h3 className="uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4, color: theme.titleColor }}>
              Average FOB Price by Origin - Heatmap
            </h3>
            <ChartHelpButton title="FOB Price Heatmap" description={helpDescription} />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-center" style={{ fontSize, color: theme.secondaryText }}>
            No price data available.<br />Select a different commodity.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-transparent">
      <div className="px-4">
      <div className="border-b py-2 flex-shrink-0" style={{ borderColor: theme.borderColor }}>
        <div className="flex items-center gap-1">
          <h3 className="uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4, color: theme.titleColor }}>
            Average FOB Price by Origin - Heatmap
          </h3>
          <ChartHelpButton title="FOB Price Heatmap" description={helpDescription} />
        </div>
        <p className="text-xs mt-1" style={{ color: theme.secondaryText }}>
          {selectedYear || '...'} vs {compareYear || '...'} - Color shows price change (green = lower, red = higher)
        </p>
      </div>
      </div>
      <div className="flex-1 flex flex-col min-h-0 px-4">
        {/* Filters Header */}
        <button
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className="w-full flex items-center justify-between py-2 border-b flex-shrink-0"
          style={{ borderColor: theme.borderSecondary }}
        >
          <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>Filters</span>
          {filtersExpanded ? <ChevronUp size={16} style={{ color: theme.secondaryText }} /> : <ChevronDown size={16} style={{ color: theme.secondaryText }} />}
        </button>

        {filtersExpanded && (
          <div className="py-2 space-y-2 flex-shrink-0">
            {/* Granularity Toggle */}
            <div>
              <label className="block mb-1 font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>Granularity</label>
              <div className="flex gap-1">
                <button
                  onClick={() => setGranularity('monthly')}
                  className={`flex-1 py-2 px-2 rounded-lg font-medium transition-colors ${
                    granularity === 'monthly'
                      ? 'bg-green-500 text-white'
                      : ''
                  }`}
                  style={{
                    fontSize: fontSize - 2,
                    ...(granularity === 'monthly' ? {} : {
                      backgroundColor: theme.controlBg,
                      borderColor: theme.controlBorder,
                      color: theme.controlText,
                      border: '1px solid'
                    })
                  }}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setGranularity('weekly')}
                  className={`flex-1 py-2 px-2 rounded-lg font-medium transition-colors ${
                    granularity === 'weekly'
                      ? 'bg-green-500 text-white'
                      : ''
                  }`}
                  style={{
                    fontSize: fontSize - 2,
                    ...(granularity === 'weekly' ? {} : {
                      backgroundColor: theme.controlBg,
                      borderColor: theme.controlBorder,
                      color: theme.controlText,
                      border: '1px solid'
                    })
                  }}
                >
                  Weekly
                </button>
              </div>
            </div>

            {/* Year and Origin Filters */}
            <div className="grid grid-cols-3 gap-2">
              <FilterDropdown
                label="Year"
                value={selectedYear || '-'}
                onClick={() => setYearModalOpen(true)}
              />
              <FilterDropdown
                label="Compare"
                value={compareYear || '-'}
                onClick={() => setCompareYearModalOpen(true)}
              />
              <FilterDropdown
                label="Origins"
                value={singleSelectionLabel(selectedOrigins, availableOptions.origins)
                  ?? (selectedOrigins.length === availableOptions.origins.length ? 'All' : `${selectedOrigins.length} selected`)}
                onClick={() => setOriginModalOpen(true)}
              />
            </div>

            {/* Product Characteristic Filters */}
            <div className="grid grid-cols-3 gap-2">
              {availableOptions.types.length > 0 && (
                <FilterDropdown
                  label="Type"
                  value={selectedType || 'Select'}
                  onClick={() => setTypeModalOpen(true)}
                />
              )}
              {availableOptions.varieties.length > 0 && (
                <FilterDropdown
                  label="Variety"
                  value={selectedVariety || 'Select'}
                  onClick={() => setVarietyModalOpen(true)}
                />
              )}
              {availableOptions.sizes.length > 0 && (
                <FilterDropdown
                  label="Size"
                  value={selectedSize || 'Select'}
                  onClick={() => setSizeModalOpen(true)}
                />
              )}
              {availableOptions.grades.length > 0 && (
                <FilterDropdown
                  label="Grade"
                  value={selectedGrade || 'Select'}
                  onClick={() => setGradeModalOpen(true)}
                />
              )}
              {availableOptions.packages.length > 0 && (
                <FilterDropdown
                  label="Package"
                  value={selectedPackage || 'Select'}
                  onClick={() => setPackageModalOpen(true)}
                />
              )}
              {availableOptions.storages.length > 0 && (
                <FilterDropdown
                  label="Storage"
                  value={selectedStorage || 'Select'}
                  onClick={() => setStorageModalOpen(true)}
                />
              )}
              {availableOptions.conditions.length > 0 && (
                <FilterDropdown
                  label="Condition"
                  value={selectedCondition || 'Select'}
                  onClick={() => setConditionModalOpen(true)}
                />
              )}
              {availableOptions.environments.length > 0 && (
                <FilterDropdown
                  label="Environment"
                  value={selectedEnvironment || 'Select'}
                  onClick={() => setEnvironmentModalOpen(true)}
                />
              )}
              {availableOptions.qualities.length > 0 && (
                <FilterDropdown
                  label="Quality"
                  value={selectedQuality || 'Select'}
                  onClick={() => setQualityModalOpen(true)}
                />
              )}
              {availableOptions.appearances.length > 0 && (
                <FilterDropdown
                  label="Appearance"
                  value={selectedAppearance || 'Select'}
                  onClick={() => setAppearanceModalOpen(true)}
                />
              )}
              {availableOptions.properties.length > 0 && (
                <FilterDropdown
                  label="Properties"
                  value={selectedProperties || 'Select'}
                  onClick={() => setPropertiesModalOpen(true)}
                />
              )}
            </div>
          </div>
        )}

        {/* Heatmap Table */}
        <div className="flex-1 min-h-0 overflow-auto mt-2">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 text-left p-1 border" style={{ fontSize: fontSize - 2, backgroundColor: theme.stickyHeaderBg, borderColor: theme.borderSecondary, color: theme.secondaryText }}>Origin</th>
                {periodLabels.map((label, idx) => (
                  <th key={idx} className="p-1 text-center border" style={{ fontSize: fontSize - 3, color: theme.secondaryText, borderColor: theme.borderSecondary }}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedOrigins.map((origin) => (
                <tr key={origin}>
                  <td className="sticky left-0 p-1 whitespace-nowrap border" style={{ fontSize: fontSize - 2, backgroundColor: theme.stickyHeaderBg, borderColor: theme.borderSecondary, color: theme.barLabelFill }}>
                    {origin}
                  </td>
                  {Array.from({ length: periodCount }, (_, i) => i + 1).map(period => {
                    const cellData = heatmapData[origin]?.[period];
                    return (
                      <td
                        key={period}
                        className={`p-1 text-center border ${cellData?.hasData ? getHeatmapColor(cellData?.delta) : isDark ? 'bg-gray-800' : 'bg-gray-100'}`}
                        style={{ fontSize: fontSize - 3, minWidth: granularity === 'weekly' ? '28px' : 'auto', borderColor: theme.borderSecondary }}
                      >
                        {cellData?.hasData ? (
                          <div>
                            <div className="font-medium" style={{ color: theme.barLabelFill }}>${cellData.price?.toFixed(0)}</div>
                            {cellData.delta !== null && (
                              <div className={cellData.delta < 0 ? (isDark ? 'text-green-300' : 'text-green-600') : (isDark ? 'text-red-300' : 'text-red-600')}>
                                {cellData.delta > 0 ? '+' : ''}{cellData.delta?.toFixed(0)}%
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: theme.secondaryText }}>-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 py-2 text-xs flex-shrink-0" style={{ color: theme.secondaryText }}>
          <span className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded ${isDark ? 'bg-[#1a4d3a]' : 'bg-green-100'}`} />≤ -10%
          </span>
          <span className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded ${isDark ? 'bg-[#2a5c4a]' : 'bg-green-200'}`} />-2% to -10%
          </span>
          <span className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded ${isDark ? 'bg-[#3a3d3b]' : 'bg-gray-300'}`} />-2% to +2%
          </span>
          <span className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded ${isDark ? 'bg-[#5c3a3a]' : 'bg-red-200'}`} />+2% to +10%
          </span>
          <span className="flex items-center gap-1">
            <span className={`w-3 h-3 rounded ${isDark ? 'bg-[#6b2d2d]' : 'bg-red-300'}`} />≥ +10%
          </span>
        </div>
      </div>

      {/* Origin Modal - Multi-select, cascades type and downstream on close */}
      <FilterModal
        isOpen={originModalOpen}
        onClose={() => {
          setOriginModalOpen(false);
          // Cascade type and downstream based on new origins selection
          if (selectedOrigins.length > 0) {
            const cascade = calculateCascade(data, { year: selectedYear, origins: selectedOrigins });
            setSelectedType(cascade.type);
            setSelectedVariety(cascade.variety);
            setSelectedSize(cascade.size);
            setSelectedGrade(cascade.grade);
            setSelectedPackage(cascade.package);
            setSelectedStorage(cascade.storage);
            setSelectedCondition(cascade.condition);
            setSelectedEnvironment(cascade.environment);
            setSelectedQuality(cascade.quality);
            setSelectedAppearance(cascade.appearance);
            setSelectedProperties(cascade.properties);
          }
        }}
        title="Select Origins"
        options={availableOptions.origins}
        selectedValues={selectedOrigins}
        onSelect={handleOriginToggle}
        multiSelect
        onSelectAll={() => setSelectedOrigins([...availableOptions.origins])}
        onClearAll={() => setSelectedOrigins([])}
      />

      {/* Year Modal - cascades to origins, type, and all downstream */}
      <FilterModal
        isOpen={yearModalOpen}
        onClose={() => setYearModalOpen(false)}
        title="Select Year"
        options={sortedYears.map(String)}
        selectedValues={selectedYear ? String(selectedYear) : ''}
        onSelect={handleYearChange}
      />

      <FilterModal
        isOpen={compareYearModalOpen}
        onClose={() => setCompareYearModalOpen(false)}
        title="Compare With Year"
        options={sortedYears.map(String)}
        selectedValues={compareYear ? String(compareYear) : ''}
        onSelect={(yearStr) => { setCompareYear(Number(yearStr)); }}
      />

      {/* Product Filter Modals - Type uses special handler */}
      <FilterModal
        isOpen={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        title="Select Type"
        options={availableOptions.types}
        selectedValues={selectedType}
        onSelect={handleTypeChange}
      />

      {/* Other filters use generic cascade handler */}
      <FilterModal
        isOpen={varietyModalOpen}
        onClose={() => setVarietyModalOpen(false)}
        title="Select Variety"
        options={availableOptions.varieties}
        selectedValues={selectedVariety}
        onSelect={(value) => handleFilterChange('variety', value)}
      />

      <FilterModal
        isOpen={sizeModalOpen}
        onClose={() => setSizeModalOpen(false)}
        title="Select Size"
        options={availableOptions.sizes}
        selectedValues={selectedSize}
        onSelect={(value) => handleFilterChange('size', value)}
      />

      <FilterModal
        isOpen={gradeModalOpen}
        onClose={() => setGradeModalOpen(false)}
        title="Select Grade"
        options={availableOptions.grades}
        selectedValues={selectedGrade}
        onSelect={(value) => handleFilterChange('grade', value)}
      />

      <FilterModal
        isOpen={packageModalOpen}
        onClose={() => setPackageModalOpen(false)}
        title="Select Package"
        options={availableOptions.packages}
        selectedValues={selectedPackage}
        onSelect={(value) => handleFilterChange('package', value)}
      />

      <FilterModal
        isOpen={storageModalOpen}
        onClose={() => setStorageModalOpen(false)}
        title="Select Storage"
        options={availableOptions.storages}
        selectedValues={selectedStorage}
        onSelect={(value) => handleFilterChange('storage', value)}
      />

      <FilterModal
        isOpen={conditionModalOpen}
        onClose={() => setConditionModalOpen(false)}
        title="Select Condition"
        options={availableOptions.conditions}
        selectedValues={selectedCondition}
        onSelect={(value) => handleFilterChange('condition', value)}
      />

      <FilterModal
        isOpen={environmentModalOpen}
        onClose={() => setEnvironmentModalOpen(false)}
        title="Select Environment"
        options={availableOptions.environments}
        selectedValues={selectedEnvironment}
        onSelect={(value) => handleFilterChange('environment', value)}
      />

      <FilterModal
        isOpen={qualityModalOpen}
        onClose={() => setQualityModalOpen(false)}
        title="Select Quality"
        options={availableOptions.qualities}
        selectedValues={selectedQuality}
        onSelect={(value) => handleFilterChange('quality', value)}
      />

      <FilterModal
        isOpen={appearanceModalOpen}
        onClose={() => setAppearanceModalOpen(false)}
        title="Select Appearance"
        options={availableOptions.appearances}
        selectedValues={selectedAppearance}
        onSelect={(value) => handleFilterChange('appearance', value)}
      />

      <FilterModal
        isOpen={propertiesModalOpen}
        onClose={() => setPropertiesModalOpen(false)}
        title="Select Properties"
        options={availableOptions.properties}
        selectedValues={selectedProperties}
        onSelect={(value) => {
          setSelectedProperties(value);
          // Update origins for properties change
          let filtered = data;
          if (selectedType) filtered = filtered.filter(item => item.type === selectedType);
          if (selectedVariety) filtered = filtered.filter(item => item.variety === selectedVariety);
          if (selectedSize) filtered = filtered.filter(item => item.item_size === selectedSize);
          if (selectedGrade) filtered = filtered.filter(item => item.grade === selectedGrade);
          if (selectedPackage) filtered = filtered.filter(item => item.package === selectedPackage);
          if (selectedStorage) filtered = filtered.filter(item => item.storage === selectedStorage);
          if (selectedCondition) filtered = filtered.filter(item => item.condition === selectedCondition);
          if (selectedEnvironment) filtered = filtered.filter(item => item.environment === selectedEnvironment);
          if (selectedQuality) filtered = filtered.filter(item => item.quality === selectedQuality);
          if (selectedAppearance) filtered = filtered.filter(item => item.appearance === selectedAppearance);
          if (value) filtered = filtered.filter(item => item.properties === value);
          const finalOrigins = getCleanOptions(filtered, 'origin', commodity);
          setSelectedOrigins(finalOrigins);
        }}
      />
    </div>
  );
};

export default FOBPriceHeatmap;
